import {
  SingleProfitCalculationApiResponse,
  Transaction,
} from '../../../../ProfitCalculations/types';
import { DeepTx, TxItem, TxType } from '../txHelpers';
import {
  fmtBalance,
  fmtDate,
  fmtPct,
  fmtPrice,
  fmtQty,
  isCustomTransactionType,
  isStopLoss,
  isTakeProfit,
  txProfitValue,
  txStableFraction,
} from './format';
import { customTransactionKind } from '../../../../../helpers/customTransactions';

const classify = (tx: Transaction): TxType => {
  if (isStopLoss(tx.transactionType)) return 'sl';
  if (isTakeProfit(tx.transactionType)) return 'tp';

  // The row's own kind, never a substring of its name: `startsWith('REMOVE')` read a
  // `CORRECTION_*` as a deposit and labelled a balance write-down "Balance added".
  switch (customTransactionKind(tx.transactionType)) {
    case 'DISTRIBUTION':
      return 'dist';
    case 'DEPOSIT':
      return 'deposit';
    case 'WITHDRAWAL':
      return 'withdrawal';
    case 'CORRECTION':
      return 'correction';
    case 'INTEREST':
      return 'interest';
    default:
      return tx.orderType === 'BUY' ? 'buy' : 'sell';
  }
};

/** Custom transactions realise no profit — the headline number is the value
 * that actually moved, in stable. */
const isCustom = (tx: Transaction): boolean =>
  isCustomTransactionType(tx.transactionType);

const headlineValue = (tx: Transaction): string =>
  isCustom(tx) ? fmtBalance(tx.stableQuantity) : fmtPct(txProfitValue(tx));

export const toTxItem = (tx: Transaction): TxItem => ({
  id: tx.id,
  type: classify(tx),
  orderType: tx.orderType,
  timestamp: tx.transactionTimestamp ?? tx.timestamp,
  date: fmtDate(tx.transactionTimestamp ?? tx.timestamp),
  price: fmtPrice(tx.orderPrice),
  profit: headlineValue(tx),
  stableShare: txStableFraction(tx),
  stableQty: fmtBalance(tx.stableQuantity),
  stableBal: fmtBalance(tx.balanceInUsdt),
  assetQty: fmtQty(tx.assetQuantity),
  assetBal: fmtQty(tx.balanceInAsset),
  fee: fmtBalance(tx.currentFee),
  totalFees: fmtBalance(tx.totalFee),
});

export const toDeepTx = (tx: Transaction): DeepTx => ({
  id: tx.id,
  type: classify(tx),
  orderType: tx.orderType,
  stableShare: txStableFraction(tx),
  date: fmtDate(tx.transactionTimestamp ?? tx.timestamp),
  price: fmtPrice(tx.orderPrice),
  profit: headlineValue(tx),
  stableQty: fmtBalance(tx.stableQuantity),
  stableProfit: isCustom(tx) ? '—' : fmtPct(tx.profitInUsdt),
  assetQty: fmtQty(tx.assetQuantity),
  assetProfit: isCustom(tx) ? '—' : fmtPct(tx.profitInAsset),
  stableBal: fmtBalance(tx.balanceInUsdt),
  assetBal: fmtQty(tx.balanceInAsset),
  fee: fmtBalance(tx.currentFee),
  totalFees: fmtBalance(tx.totalFee),
  extraStat: isStopLoss(tx.transactionType)
    ? { label: 'Stop loss threshold', value: fmtPct(tx.priceThreshold ?? 0) }
    : isTakeProfit(tx.transactionType)
    ? { label: 'Take profit size', value: fmtPct(tx.takeProfitSize ?? 0) }
    : null,
});

export const buildTransactionList = (
  spc: SingleProfitCalculationApiResponse,
): {
  list: TxItem[];
  deepByTxId: Record<number, DeepTx>;
  orderedIds: number[];
} => {
  // Custom transactions (subscription only) belong in the list even though they
  // are excluded from every aggregation — without them a CPC distribution that
  // sold the whole position is invisible and the balances appear to jump on
  // their own. Empty on an SPC result, so that page is untouched.
  const sorted = [...spc.transactions, ...(spc.customTransactions ?? [])].sort(
    (a, b) =>
      (b.transactionTimestamp ?? b.timestamp) -
      (a.transactionTimestamp ?? a.timestamp),
  );

  const list = sorted.map(toTxItem);
  const deepByTxId: Record<number, DeepTx> = {};
  for (const tx of sorted) deepByTxId[tx.id] = toDeepTx(tx);

  return {
    list,
    deepByTxId,
    orderedIds: sorted.map((tx) => tx.id),
  };
};

export const avgTradeDurationMs = (
  spc: SingleProfitCalculationApiResponse,
): number => {
  if (spc.transactions.length < 2) return 0;
  const sorted = [...spc.transactions].sort(
    (a, b) =>
      (a.transactionTimestamp ?? a.timestamp) -
      (b.transactionTimestamp ?? b.timestamp),
  );
  let total = 0;
  for (let i = 1; i < sorted.length; i++) {
    total +=
      (sorted[i].transactionTimestamp ?? sorted[i].timestamp) -
      (sorted[i - 1].transactionTimestamp ?? sorted[i - 1].timestamp);
  }
  return total / (sorted.length - 1);
};

export const avgProfit = (spc: SingleProfitCalculationApiResponse): number => {
  const txs = spc.transactions;
  if (!txs.length) return 0;
  return txs.reduce((acc, tx) => acc + txProfitValue(tx), 0) / txs.length;
};
